import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Trophy, Skull, Users, Clock, Maximize2 } from "lucide-react";

const POLL_INTERVAL_MS = 4000;

const HostStage = () => {
  const { hostToken } = useParams();
  const [data, setData] = useState(null);
  const [timerSec, setTimerSec] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPreset, setTimerPreset] = useState(600); // 10 min default

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/host/${hostToken}/stage`);
      setData(r.data);
    } catch (e) {
      // leave stale
    }
  }, [hostToken]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const i = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(i);
  }, [refresh]);

  // Timer tick
  useEffect(() => {
    if (!timerRunning) return;
    const i = setInterval(() => {
      setTimerSec((s) => {
        if (s <= 1) {
          setTimerRunning(false);
          toast("Round over.", { className: "font-hand" });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [timerRunning]);

  const toggleClue = async (clue_set, index, revealed) => {
    try {
      await api.post(`/host/${hostToken}/reveal/${revealed ? "hide" : "clue"}`, { clue_set, index });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleFlag = async (key) => {
    try {
      await api.post(`/host/${hostToken}/reveal/toggle`, { [key]: !data.reveal_state[key] });
      refresh();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const goFullscreen = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  if (!data) {
    return <div className="grain min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#8b0000]" /></div>;
  }

  return (
    <div className="grain min-h-screen" data-testid="host-stage-page">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl">case #{data.case_number} · live stage</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-1">{data.case_title}</h1>
            <div className="text-sm text-[#a39797] mt-1">{data.host_name} · {data.event_date}</div>
          </div>
          <div className="flex gap-2 items-center">
            <button className="btn-ghost text-xs" onClick={goFullscreen} data-testid="fullscreen-btn">
              <Maximize2 size={14} /> Big screen
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Column 1: Timer + Scoreboard */}
          <div className="space-y-4">
            <div className="card-noir p-6" data-testid="timer-card">
              <div className="flex items-center gap-2 text-xs tracking-widest text-[#a39797] mb-3"><Clock size={14} /> ROUND TIMER</div>
              <div className="font-display text-6xl text-[#d4af37] tabular-nums text-center" data-testid="timer-display">
                {fmt(timerSec)}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                {[300, 600, 900].map((p) => (
                  <button key={p} onClick={() => { setTimerPreset(p); setTimerSec(p); setTimerRunning(false); }}
                    className={`text-xs px-2 py-2 border transition-colors ${
                      timerPreset === p ? "border-[#d4af37] text-[#d4af37]" : "border-[#2d2424] text-[#a39797] hover:border-[#8b0000]"
                    }`}
                    data-testid={`timer-preset-${p}`}>
                    {p / 60}m
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button className="btn-primary text-sm flex-1" onClick={() => setTimerRunning((v) => !v)}
                  data-testid="timer-toggle">
                  {timerRunning ? "Pause" : "Start"}
                </button>
                <button className="btn-ghost text-sm" onClick={() => { setTimerSec(timerPreset); setTimerRunning(false); }}
                  data-testid="timer-reset">
                  Reset
                </button>
              </div>
            </div>

            <div className="card-noir p-6" data-testid="scoreboard">
              <div className="flex items-center gap-2 text-xs tracking-widest text-[#a39797] mb-3"><Trophy size={14} /> SCOREBOARD</div>
              {(data.leaderboard || []).length === 0 ? (
                <div className="text-sm text-[#a39797]">No guesses submitted yet.</div>
              ) : (
                <ol className="space-y-2">
                  {data.leaderboard.map((p, i) => (
                    <li key={p.player_token} className="flex items-center justify-between text-sm" data-testid={`leader-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[#d4af37] font-display">{i + 1}.</span>
                        <span className="text-[#f4f0ec]">{p.player_name}</span>
                      </div>
                      <div className="text-xs text-[#a39797]">
                        {p.score?.correct || 0}/3
                        {p.score?.killer && <span className="text-[#d4af37] ml-2">✓ killer</span>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Column 2: Clue reveal controls */}
          <div className="space-y-4">
            <div className="card-noir p-6" data-testid="clues-a">
              <div className="flex items-center gap-2 text-xs tracking-widest text-[#8b0000] mb-3">CLUE SET A</div>
              <div className="space-y-2">
                {(data.clues_a || []).map((c) => (
                  <button key={c.index}
                    onClick={() => toggleClue("a", c.index, c.revealed)}
                    className={`w-full text-left p-3 border transition-colors text-sm ${
                      c.revealed ? "border-[#d4af37] bg-[#d4af37]/5 text-[#f4f0ec]" :
                      "border-[#2d2424] text-[#a39797] hover:border-[#8b0000]"
                    }`}
                    data-testid={`toggle-clue-a-${c.index}`}>
                    <div className="flex items-start gap-2">
                      {c.revealed ? <Eye size={14} className="text-[#d4af37] mt-0.5" /> : <EyeOff size={14} className="text-[#a39797] mt-0.5" />}
                      <span>{c.text}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card-noir p-6" data-testid="clues-b">
              <div className="flex items-center gap-2 text-xs tracking-widest text-[#8b0000] mb-3">CLUE SET B</div>
              <div className="space-y-2">
                {(data.clues_b || []).map((c) => (
                  <button key={c.index}
                    onClick={() => toggleClue("b", c.index, c.revealed)}
                    className={`w-full text-left p-3 border transition-colors text-sm ${
                      c.revealed ? "border-[#d4af37] bg-[#d4af37]/5 text-[#f4f0ec]" :
                      "border-[#2d2424] text-[#a39797] hover:border-[#8b0000]"
                    }`}
                    data-testid={`toggle-clue-b-${c.index}`}>
                    <div className="flex items-start gap-2">
                      {c.revealed ? <Eye size={14} className="text-[#d4af37] mt-0.5" /> : <EyeOff size={14} className="text-[#a39797] mt-0.5" />}
                      <span>{c.text}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Column 3: Players + Final Reveal */}
          <div className="space-y-4">
            <div className="card-noir p-6" data-testid="players-card">
              <div className="flex items-center gap-2 text-xs tracking-widest text-[#a39797] mb-3"><Users size={14} /> PLAYERS</div>
              <div className="space-y-2">
                {(data.players || []).map((p) => (
                  <div key={p.player_token} className="flex items-center justify-between text-sm" data-testid={`player-${p.player_token}`}>
                    <div>
                      <div className="text-[#f4f0ec]">{p.player_name}</div>
                      <div className="text-xs text-[#a39797]">{p.archetype}</div>
                    </div>
                    <div className="text-xs">
                      {p.guess_submitted ? (
                        <span className="text-[#d4af37]">✓ guess in</span>
                      ) : (
                        <span className="text-[#a39797]">waiting</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-noir p-6" data-testid="reveal-controls">
              <div className="flex items-center gap-2 text-xs tracking-widest text-[#8b0000] mb-3"><Skull size={14} /> REVEAL CONTROLS</div>
              <div className="space-y-2">
                <button onClick={() => toggleFlag("victim_revealed")}
                  className={`w-full p-3 border text-left transition-colors ${
                    data.reveal_state.victim_revealed ? "border-[#d4af37] bg-[#d4af37]/5" : "border-[#2d2424] hover:border-[#8b0000]"
                  }`}
                  data-testid="toggle-victim">
                  <div className="text-sm text-[#f4f0ec]">Victim: {data.victim_name}</div>
                  <div className="text-xs text-[#a39797]">{data.reveal_state.victim_revealed ? "Visible on all phones" : "Tap to reveal"}</div>
                </button>
                <button onClick={() => toggleFlag("reveal_revealed")}
                  className={`w-full p-3 border text-left transition-colors ${
                    data.reveal_state.reveal_revealed ? "border-[#d4af37] bg-[#d4af37]/5" : "border-[#2d2424] hover:border-[#8b0000]"
                  }`}
                  data-testid="toggle-final-reveal">
                  <div className="text-sm text-[#f4f0ec]">Final reveal (killer)</div>
                  <div className="text-xs text-[#a39797]">{data.reveal_state.reveal_revealed ? "Pushed to phones" : "Read first, then tap"}</div>
                </button>
                <button onClick={() => toggleFlag("awards_revealed")}
                  className={`w-full p-3 border text-left transition-colors ${
                    data.reveal_state.awards_revealed ? "border-[#d4af37] bg-[#d4af37]/5" : "border-[#2d2424] hover:border-[#8b0000]"
                  }`}
                  data-testid="toggle-awards">
                  <div className="text-sm text-[#f4f0ec]">Verdict cards</div>
                  <div className="text-xs text-[#a39797]">{data.reveal_state.awards_revealed ? "Awards live" : "Hand out awards"}</div>
                </button>
              </div>
            </div>

            {data.reveal_state.reveal_revealed && (
              <div className="card-noir p-6 border-[#d4af37]" data-testid="killer-banner">
                <div className="text-xs tracking-widest text-[#d4af37]">KILLER CONFIRMED</div>
                <div className="font-display text-3xl mt-1">{data.killer_name}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostStage;
